import { SortingOption, useArrayDataSource } from '@epam/uui-core';
import { useCallback, useState } from 'react';
import { BroadcastDeliveryItemWithId } from 'models/broadcast.model';
import { DataTable, FlexRow, Panel } from '@epam/uui';
import FilterDropdown from '../../../common/Filters/FilterDropdown';
import css from '../../../common/CardFilters/CardFilters.module.scss';
import { FlexCell, SearchInput } from '@epam/promo';
import columns from './tableColumns';
import filterSettings from './filterSettings';
import { sortBy } from '../../../../hooks/helpers/filtersHelpers';

interface BroadcastLogsProps {
  reportData: BroadcastDeliveryItemWithId[];
  isReportLoading: boolean;
}

interface Filter {
  search?: string;
  status?: string[];
  errorCode?: string[];
}

interface DataSourceState {
  sorting?: SortingOption<keyof BroadcastDeliveryItemWithId>[];
  filter?: Filter;
}

const getFilter =
  (filter: Filter) =>
  ({
    status,
    errorCode,
    errorMessage,
    chatId,
  }: BroadcastDeliveryItemWithId) => {
    if (filter?.search) {
      const searchValue = filter.search.toLowerCase().trim();

      return (
        errorMessage?.toLowerCase().includes(searchValue) ||
        chatId?.toLowerCase().indexOf(searchValue) === 0
      );
    }
    if (filter?.errorCode?.length) return filter.errorCode.includes(errorCode);
    if (filter?.status?.length) return filter.status.includes(status);

    return true;
  };

const BroadcastLogs = ({ reportData, isReportLoading }: BroadcastLogsProps) => {
  const [dataSourceState, setDataSourceState] = useState<DataSourceState>({
    sorting: [],
    filter: {
      search: '',
      status: [],
      errorCode: [],
    },
  });

  const dataSource = useArrayDataSource(
    {
      items: reportData,
      getFilter,
      sortBy: sortBy,
      getRowOptions: () => ({
        checkbox: { isVisible: false },
        isSelectable: true,
      }),
    },
    [reportData],
  );

  const view = dataSource.useView(dataSourceState, setDataSourceState);

  const setFilter = useCallback((filter: Partial<Filter> | undefined) => {
    if (!filter) {
      setDataSourceState((state) => ({ ...state, filter: {} }));
    } else {
      setDataSourceState((state) => ({
        ...state,
        filter: { ...state.filter, ...filter },
      }));
    }
  }, []);

  return (
    <Panel>
      <FlexRow columnGap="12" padding="12" vPadding="18">
        <FlexCell cx={css.search__container} grow={2}>
          <SearchInput
            cx={css.search__input}
            debounceDelay={1000}
            placeholder="Search by id or error message"
            value={dataSourceState.filter?.search ?? ''}
            onValueChange={(newValue) => setFilter({ search: newValue ?? '' })}
          />
        </FlexCell>
        <FlexCell grow={1}>
          <FilterDropdown<Filter | undefined>
            filter={dataSourceState.filter}
            filterSettings={filterSettings}
            setFilter={setFilter}
          />
        </FlexCell>
      </FlexRow>

      <DataTable
        allowColumnsReordering={true}
        allowColumnsResizing={true}
        columns={columns}
        getRows={view.getVisibleRows}
        headerTextCase="upper"
        isReloading={isReportLoading}
        showColumnsConfig={true}
        value={dataSourceState}
        onValueChange={setDataSourceState}
      />
    </Panel>
  );
};

export default BroadcastLogs;
